import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils/classNames';

/**
 * GGC V2. Button — Figma node 132:517 family (Component 01).
 *
 * 9 visual styles × 4 sizes. Pill variants share padding/text with `default`
 * size but use `r-pill` corners. Text is Inter Semi-Bold.
 *
 * Use this for new GGC V2. screens. The legacy `@/components/ui/button`
 * remains for pages not yet migrated.
 */
const ggcButtonVariants = cva(
  'inline-flex items-center justify-center gap-[4px] font-ggc font-semibold whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ggc-blue/40 focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        primary: 'bg-ggc-cta-blue text-white hover:bg-ggc-cta-blue/90',
        default:
          'bg-white border border-ggc-border-gray text-ggc-text-gray hover:bg-ggc-bg-base hover:text-ggc-text-primary',
        secondary:
          'bg-white border border-ggc-border-gray-dark text-ggc-text-primary hover:bg-ggc-bg-surface',
        ghost: 'text-ggc-text-gray hover:bg-ggc-bg-base',
        danger: 'bg-ggc-red text-white hover:bg-ggc-red/90',
        success: 'bg-ggc-green text-white hover:bg-ggc-green/90',
        warning: 'bg-ggc-orange text-white hover:bg-ggc-orange/90',
        dark: 'bg-ggc-main text-white hover:bg-ggc-main/90',
        'primary-pill': 'bg-ggc-cta-blue text-white hover:bg-ggc-cta-blue/90',
        'secondary-pill':
          'bg-white border border-ggc-border-gray-dark text-ggc-text-primary hover:bg-ggc-bg-surface',
      },
      size: {
        xs: 'h-[20px] px-[8px] py-[2px] ggc-text-micro-bold rounded-ggc-sm',
        sm: 'h-[26px] px-[8px] py-[5px] text-[10px] rounded-ggc-md',
        default: 'px-[16px] py-[8px] text-[12px] rounded-ggc-md',
        lg: 'px-[22px] py-[11px] text-[14px] rounded-ggc-md',
        icon: 'h-7 w-7 p-0 text-[12px] rounded-ggc-md',
      },
    },
    compoundVariants: [
      // Pill variants override rounding regardless of size
      { variant: 'primary-pill', className: 'rounded-ggc-pill' },
      { variant: 'secondary-pill', className: 'rounded-ggc-pill' },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  }
);

export interface GgcButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof ggcButtonVariants> {
  asChild?: boolean;
}

const GgcButton = React.forwardRef<HTMLButtonElement, GgcButtonProps>(
  (
    { className, variant, size, asChild = false, type = 'button', ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        type={asChild ? undefined : type}
        ref={ref}
        className={cn(ggcButtonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
GgcButton.displayName = 'GgcButton';

export { GgcButton };
